import React, { useState, useEffect } from "react";
import api from "../../api/api";
import PlaceOfBirthField from "../../components/PlaceOfBirthField";
import "../../styles/userProfile.css";

const UserProfile = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    mobile: '',
    name: '',
    email: '',
    city: '',
    state: '',
    country: 'India'
  });

  const userid = JSON.parse(localStorage.getItem("user"))?.userid;

  // Fetch user data on component mount
  useEffect(() => {
    const fetchUserData = async () => {
      if (!userid) return;
      
      setLoading(true);
      try {
        const response = await api.get(`/user/${userid}`);
        if (response.data && response.data.success) {
          const userData = response.data.data;
          setFormData({
            mobile: userData.mobile || '',
            name: userData.name || '',
            email: userData.email || '',
            city: userData.city || '',
            state: userData.state || '',
            country: userData.country || 'India'
          });
        }
      } catch (error) {
        setMessage({ 
          type: 'error', 
          text: 'Failed to load user data. Please refresh the page.' 
        });
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [userid]);

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    // Clear messages
    setMessage({ type: '', text: '' });
  };

  // Handle city selection and auto-populate state
  const handleCityChange = (cityValue) => {
    setFormData(prev => ({ ...prev, city: cityValue }));
    
    // Auto-populate state from search_key format: City,District,State,Country
    if (cityValue.includes(',')) {
      const parts = cityValue.split(',');
      if (parts.length >= 3) {
        const stateName = parts[2].trim(); // State is at index 2
        setFormData(prev => ({ ...prev, state: stateName }));
      }
    }

    // Clear error for city field
    if (errors.city) {
      setErrors(prev => ({ ...prev, city: '' }));
    }
    setMessage({ type: '', text: '' });
  };

  // Validation functions
  const validateEmail = (email) => {
    if (!email) return ''; // Email is optional
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) ? '' : 'Please enter a valid email address';
  };

  const validateName = (name) => {
    if (!name || !name.trim()) return 'Name is required';
    if (name.trim().length < 2) return 'Name must be at least 2 characters long';
    if (!/^[a-zA-Z\s]+$/.test(name.trim())) return 'Name can only contain letters and spaces';
    return '';
  };

  const validateCity = (city) => {
    if (!city || !city.trim()) return 'City is required';
    return '';
  };

  const validateState = (state) => {
    if (!state || !state.trim()) return 'State is required';
    return '';
  };

  // Validate all fields
  const validateForm = () => {
    const newErrors = {
      name: validateName(formData.name),
      email: validateEmail(formData.email),
      city: validateCity(formData.city),
      state: validateState(formData.state)
    };

    // Remove empty error messages
    Object.keys(newErrors).forEach(key => {
      if (!newErrors[key]) delete newErrors[key];
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setMessage({ 
        type: 'error', 
        text: 'Please fix the errors below before submitting.' 
      });
      return;
    }

    setSaving(true);
    try {
      const updateData = {
        name: formData.name.trim(),
        email: formData.email.trim() || null,
        city: formData.city.trim(),
        state: formData.state.trim(),
        country: formData.country
      };

      const response = await api.put(`/user/${userid}`, updateData);
      
      if (response.data && response.data.success) {
        setMessage({ 
          type: 'success', 
          text: 'Profile updated successfully!' 
        });
        
        // Update localStorage if name was changed
        const user = JSON.parse(localStorage.getItem("user"));
        if (user) {
          user.name = formData.name.trim();
          localStorage.setItem("user", JSON.stringify(user));
        }
      } else {
        throw new Error(response.data?.message || 'Failed to update profile');
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: `Failed to update profile: ${error.response?.data?.message || error.message}` 
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="user-profile-page">
        <div className="user-profile-container">
          <div className="user-profile-loading">
            <div className="spinner"></div>
            <span>Loading your profile...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="user-profile-page">
      <div className="user-profile-container">
        {/* Header Section */}
        <div className="user-profile-header">
          <div className="user-profile-header-content">
            <h1 className="user-profile-title">
              <span className="user-profile-icon">👤</span>
              User Profile
            </h1>
            <p className="user-profile-subtitle">
              Update your personal information and contact details
            </p>
          </div>
        </div>

        {/* Profile Form */}
        <div className="user-profile-form-card">
          {/* Message Display */}
          {message.text && (
            <div className={`user-profile-message ${message.type}`}>
              <span className="user-profile-message-icon">
                {message.type === 'success' ? '✅' : '⚠️'}
              </span>
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit} className="user-profile-form">
            <div className="user-profile-form-grid">
              {/* Mobile Number (Read-only) */}
              <div className="user-profile-form-group">
                <label className="user-profile-form-label">
                  <span className="user-profile-label-icon">📱</span>
                  Mobile Number
                </label>
                <input
                  type="tel"
                  value={formData.mobile}
                  className="user-profile-input"
                  disabled
                  placeholder="Mobile number"
                />
              </div>

              {/* Name */}
              <div className="user-profile-form-group">
                <label className="user-profile-form-label required">
                  <span className="user-profile-label-icon">👤</span>
                  Full Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className={`user-profile-input ${errors.name ? 'error' : ''}`}
                  placeholder="Enter your full name"
                  disabled={saving}
                />
                {errors.name && (
                  <div className="user-profile-error-message">
                    <span className="user-profile-error-icon">⚠️</span>
                    {errors.name}
                  </div>
                )}
              </div>

              {/* Email */}
              <div className="user-profile-form-group">
                <label className="user-profile-form-label">
                  <span className="user-profile-label-icon">📧</span>
                  Email Address
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={`user-profile-input ${errors.email ? 'error' : ''}`}
                  placeholder="Enter your email address"
                  disabled={saving}
                />
                {errors.email && (
                  <div className="user-profile-error-message">
                    <span className="user-profile-error-icon">⚠️</span>
                    {errors.email}
                  </div>
                )}
              </div>

              {/* City */}
              <div className="user-profile-form-group">
                <label className="user-profile-form-label required">
                  <span className="user-profile-label-icon">🏙️</span>
                  City
                </label>
                <PlaceOfBirthField
                  value={formData.city}
                  onChange={handleCityChange}
                  placeholder="Search for place, district, or state..."
                />
                {errors.city && (
                  <div className="user-profile-error-message">
                    <span className="user-profile-error-icon">⚠️</span>
                    {errors.city}
                  </div>
                )}
              </div>

              {/* State */}
              <div className="user-profile-form-group">
                <label className="user-profile-form-label required">
                  <span className="user-profile-label-icon">🗺️</span>
                  State
                </label>
                <input
                  type="text"
                  value={formData.state}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                  className={`user-profile-input ${errors.state ? 'error' : ''}`}
                  placeholder="Enter your state"
                  disabled={saving}
                />
                {errors.state && (
                  <div className="user-profile-error-message">
                    <span className="user-profile-error-icon">⚠️</span>
                    {errors.state}
                  </div>
                )}
              </div>

              {/* Country (Auto-filled) */}
              <div className="user-profile-form-group">
                <label className="user-profile-form-label">
                  <span className="user-profile-label-icon">🌍</span>
                  Country
                </label>
                <input
                  type="text"
                  value={formData.country}
                  className="user-profile-input"
                  disabled
                  placeholder="Country"
                />
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="user-profile-submit-btn"
              disabled={saving}
            >
              {saving ? (
                <>
                  <div className="user-profile-btn-spinner"></div>
                  Updating Profile...
                </>
              ) : (
                <>
                  <span className="user-profile-btn-icon">💾</span>
                  Update Profile
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;